import { useMemo } from "react";
import { useOnboardingProgress } from "../../workflows/api/useOnboardingProgress";
import { useWorkflowProgress } from "../../workflows/api/useWorkflowProgress";
import { useAppSelector } from "../../../redux/hooks";
import { buildIngestionModel } from "../adapter";
import { sqprNothingRequested, sqprStillImporting } from "../sqprSettlement";
import type { IngestionModel, PhaseStatus } from "../types";

const POLL_MS = 1000 * 15; // matches useOnboardingProgress

const normalizeStatus = (status: "PENDING" | "RUNNING" | "SUCCESS" | "FAILED" | undefined): PhaseStatus | "none" => {
  if (!status) return "none";
  if (status === "SUCCESS") return "done";
  if (status === "FAILED") return "failed";
  return "running"; // PENDING | RUNNING
};

export const useIngestionProgress = (): { model: IngestionModel; isLoading: boolean } => {
  const { recentWorkflow, backfillWorkflow, sellerAnalyticsWorkflow, recent, backfill, shouldShow, isLoading } =
    useOnboardingProgress();
  const isStaff = useAppSelector((state) => state.user.is_staff);
  const isVendor = useAppSelector((state) => state.user.organization_type === "vendor");

  const sellerAnalyticsId =
    sellerAnalyticsWorkflow?.tracking_id ?? recent.data?.seller_analytics_workflow_id ?? undefined;
  const sellerAnalytics = useWorkflowProgress(sellerAnalyticsId, { refetchInterval: POLL_MS });

  const recentPercent = recent.data?.percent ?? null;
  const backfillPercent = backfill.data?.percent ?? null;
  const recentPhase = recent.data?.phase ?? null;
  const enrichmentSqpr = backfill.data?.sqpr_progress ?? null;
  const enrichmentSqprSettled =
    !!enrichmentSqpr && !sqprStillImporting(enrichmentSqpr, backfill.data?.sqpr_dispatched_at);
  const enrichmentSqprNothingRequested = sqprNothingRequested(enrichmentSqpr, backfill.data?.sqpr_dispatched_at);
  const recentUsable = recent.data?.is_usable ?? null;
  const sellerAnalyticsStatus = normalizeStatus(sellerAnalytics.data?.status);
  const sellerAnalyticsReport = sellerAnalytics.data?.report_progress;
  const sellerAnalyticsPercent =
    sellerAnalytics.data?.percent ??
    (sellerAnalyticsReport && sellerAnalyticsReport[1] > 0 ?
      (sellerAnalyticsReport[0] / sellerAnalyticsReport[1]) * 100
    : null);

  const model = useMemo(
    () =>
      buildIngestionModel({
        recentWorkflow,
        backfillWorkflow,
        recentPercent,
        backfillPercent,
        recentPhase,
        enrichmentSqpr,
        enrichmentSqprSettled,
        enrichmentSqprNothingRequested,
        sellerAnalyticsStatus,
        sellerAnalyticsPercent,
        recentUsable,
        shouldShow,
        isStaff,
        isVendor,
      }),
    [
      recentWorkflow,
      backfillWorkflow,
      recentPercent,
      backfillPercent,
      recentPhase,
      enrichmentSqpr,
      enrichmentSqprSettled,
      enrichmentSqprNothingRequested,
      sellerAnalyticsStatus,
      sellerAnalyticsPercent,
      recentUsable,
      shouldShow,
      isStaff,
      isVendor,
    ],
  );

  return { model, isLoading };
};
